/**
 * Taking calls from the ACD — the rules the phone's availability control follows, and the refetch
 * hints that keep it true. Pure, so `test/acd.test.ts` pins them.
 *
 * What a rep's state IS comes from the event-tracker's record of it (`GET /acd/me`), never from the
 * button they last pressed. The ACD applies a command asynchronously, after core-api has answered
 * 202, and a Ready that did not take — no lines staffed, the broker down — must not read as Ready.
 * So pressing a button publishes an intent, and the label changes when the record does.
 */
import type { AgentAction, AgentLine, AgentState, AgentStateRecord, QueueSession } from "./types.js";

/** Why a rep is not taking calls, as the ACD and the reports record it. */
export const BREAK_REASONS = [
  { code: "break", label: "Break" },
  { code: "lunch", label: "Lunch" },
  { code: "meeting", label: "Meeting" },
  { code: "training", label: "Training" },
] as const;

export type AvailabilityTone = "success" | "warning" | "accent" | "neutral";

/** One command for the rep's own phone (`POST /acd/agent/{action}`): on one line, or on all of
 *  their lines while `queue` is null. */
export interface AgentStep {
  action: AgentAction;
  queue: string | null;
  /** The break reason, for a pause. */
  code?: string | null;
  /** With a login: true is My calls only, false every call on the line; left out, the rep keeps
   *  what they had (ADR-037). */
  ownCallsOnly?: boolean;
  /** With a login: true is My calls only until the rep answers a call meant for them, false ends
   *  it; left out, the rep keeps what they had (ADR-070). */
  ownCallsNext?: boolean;
}

/** How the control reads and what it may do right now. Each action says why it does not apply,
 *  so a dimmed menu item can carry its reason instead of just being grey. */
export interface Availability {
  /** Logged in to at least one line. */
  signedIn: boolean;
  /** The ACD's effective state — across every line — or null while signed out. */
  state: string | null;
  label: string;
  /** One word, for the phone's 56px rail. */
  short: string;
  tone: AvailabilityTone;
  /** What Ready sends, in order, and what it does. Without steps it does not apply, and `detail`
   *  says why. */
  ready: { steps: AgentStep[]; detail: string };
  /** What My calls only sends, the same way (ADR-070): Ready, but only for calls meant for the
   *  rep, until they answer one. */
  myCalls: { steps: AgentStep[]; detail: string };
  /** The lines a signed-in rep staffs and is not signed in to yet (`linesToJoin`). */
  notYetOn: AgentLine[];
  breakBlocked: string | null;
  signOutBlocked: string | null;
  /** The rep takes only the calls meant for them, on at least one line (ADR-037). */
  ownCallsOnly: boolean;
  /** The rep has a number of their own on a campaign that keeps such reps to their own callers
   *  (ADR-037, amended 2026-09-17). Their Ready signs them in that way on that campaign's lines and
   *  for every call on the rest. The phone still says only Ready: the campaign decides. */
  mine: boolean;
  /** The rep chose My calls only for their next call (ADR-070), which the ACD ends when they
   *  answer a call meant for them. Unlike the campaign's rule, the phone says so. */
  ownCallsNext: boolean;
}

/** The effective states the event-tracker writes (`services/shared/state/queues.py`). */
const STATES: Record<string, { label: string; short: string; tone: AvailabilityTone }> = {
  available: { label: "Ready", short: "Ready", tone: "success" },
  ringing: { label: "A call is ringing", short: "Ringing", tone: "accent" },
  busy: { label: "On a call", short: "On call", tone: "accent" },
  // The line's disposition window: the call has ended and the ACD waits for the outcome.
  disposition: { label: "Choosing a disposition", short: "Wrap-up", tone: "warning" },
  wrapup: { label: "Wrap-up", short: "Wrap-up", tone: "warning" },
  paused: { label: "On a break", short: "Break", tone: "warning" },
  // The dialer is placing a call for this rep (ADR-019); the lead has not answered yet.
  dialing: { label: "Dialing a lead", short: "Dialing", tone: "accent" },
  busy_external: { label: "On another call", short: "On call", tone: "accent" },
  offline: { label: "Signed in, phone offline", short: "Offline", tone: "neutral" },
};

/** A call is up or on its way: signing out now would strand it, and Ready joins no line over it.
 *  The ACD does not count the dialer placing a call as one, so a line joined then could offer the
 *  rep a caller before the lead answers. */
const ENGAGED = new Set(["ringing", "busy", "dialing", "busy_external"]);

export function breakLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return BREAK_REASONS.find((r) => r.code === code)?.label ?? code;
}

/** Any rep's effective state in the phone's words — the live floor shows the same ones. A state
 *  this console does not know is shown as the ACD spelled it. */
export function stateText(
  state: string,
  code?: string | null,
): { label: string; short: string; tone: AvailabilityTone } {
  const known = STATES[state] ?? { label: state, short: state, tone: "neutral" as const };
  const reason = state === "paused" ? breakLabel(code) : null;
  return { ...known, label: reason && reason !== "Break" ? `${known.label} · ${reason}` : known.label };
}

/** A line in the rep's words: its campaign, and a campaign's closer line as its closers. */
export function lineName(line: AgentLine): string {
  const name = line.campaign_name ?? line.name;
  return line.purpose === "closer" ? `${name} · Closers` : name;
}

/** Lines as a sentence names them: "A", "A and B", "A, B and C". */
export function linesText(lines: readonly AgentLine[]): string {
  return listText(lines.map(lineName));
}

function listText(all: readonly string[]): string {
  const names = [...new Set(all)];
  if (names.length < 2) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The lines where Ready keeps the rep to their own callers (ADR-037, amended 2026-09-17): the lines
 * of a campaign where they have a number of their own, when the campaign's routing says so. On
 * their other lines Ready takes every call.
 */
export function mineLines(me: AgentState): AgentLine[] {
  return me.lines.filter(
    (line) => (line.own_numbers ?? []).length > 0 && line.own_calls_only_allowed !== false,
  );
}

/**
 * The lines the rep staffs and is not signed in to: a line an admin added them to after they
 * pressed Ready. Only lines they staff — one they are still in on the ACD's record but no longer
 * staff is not a line to sign in to again.
 */
export function linesToJoin(me: AgentState): AgentLine[] {
  const joined = new Set(me.state?.queues ?? []);
  return me.lines.filter((line) => !joined.has(line.name));
}

/** What My calls only waits for, in the menu's words (ADR-070): a call to the rep's own number,
 *  or, for a rep who has none, a caller a colleague sends them by name. */
function myCallsDetail(me: AgentState): string {
  return (me.numbers ?? []).length > 0
    ? "Only calls to your number, until you answer one"
    : "Only callers sent to you by name, until you answer one";
}

export function availability(me: AgentState): Availability {
  const record = me.state;
  const signedIn = record !== null && record.queues.length > 0;
  const mine = mineLines(me);
  if (!signedIn) {
    const none = { steps: [], detail: "You do not staff any lines yet. An admin adds you to a campaign." };
    return {
      signedIn: false,
      state: null,
      label: "Not taking calls",
      short: "Off",
      tone: "neutral",
      ready:
        me.lines.length === 0
          ? none
          : { steps: signInSteps(me, mine, false), detail: "Sign in to every line you staff" },
      myCalls:
        me.lines.length === 0
          ? none
          : { steps: signInSteps(me, mine, true), detail: myCallsDetail(me) },
      notYetOn: [],
      breakBlocked: "Sign in to your lines first",
      signOutBlocked: "You are not signed in",
      ownCallsOnly: false,
      mine: mine.length > 0,
      ownCallsNext: false,
    };
  }
  const own = (record.ownCallsOnly ?? []).length > 0;
  const next = (record.ownCallsNext ?? []).length > 0;
  // Ready is Ready, whichever calls the campaign routes to the rep (ADR-037, amended 2026-09-17).
  // My calls only is the rep's own choice, so a free rep on it reads so (ADR-070).
  const known =
    next && record.state === "available"
      ? { label: "Ready · My calls only", short: "Mine", tone: "success" as const }
      : (STATES[record.state] ?? { label: record.state, short: record.state, tone: "neutral" as const });
  const paused = record.state === "paused";
  const reason = paused ? breakLabel(record.code) : null;
  const missing = linesToJoin(me);
  const label = reason && reason !== "Break" ? `${known.label} · ${reason}` : known.label;
  const myCalls = readyFor(me, record, mine, missing, true);
  return {
    signedIn: true,
    state: record.state,
    label,
    short: known.short,
    tone: known.tone,
    ready: readyFor(me, record, mine, missing, false),
    myCalls: { steps: myCalls.steps, detail: myCallsDetail(me) },
    notYetOn: missing,
    breakBlocked: paused ? "You are on a break already" : null,
    signOutBlocked: ENGAGED.has(record.state) ? "Finish your call first" : null,
    ownCallsOnly: own,
    mine: mine.length > 0,
    ownCallsNext: next,
  };
}

/**
 * Ready, or My calls only (`next`, ADR-070), for a rep who is signed out: their own lines first,
 * each saying so; then one login for the rest, which keeps the lines just joined as they are.
 */
function signInSteps(me: AgentState, mine: AgentLine[], next: boolean): AgentStep[] {
  const nextField = next ? { ownCallsNext: true } : {};
  if (mine.length === 0) return [{ action: "login", queue: null, ...nextField }];
  return [
    ...mine.map(
      (line): AgentStep => ({ action: "login", queue: line.name, ownCallsOnly: true, ...nextField }),
    ),
    ...(mine.length < me.lines.length ? [{ action: "login" as const, queue: null, ...nextField }] : []),
  ];
}

/**
 * Ready for a rep signed in, or My calls only (`next`, ADR-070): each line as their Ready has it
 * (My calls only on their own lines, every call on the rest) and My calls only for the next call
 * on or off, a login only where that changes, back from a break, and a line they staff and are not
 * on yet joined as its campaign has it (`readyWhileSignedIn`).
 *
 * The record's `ownCallsOnly` counts a line on My calls only for the next call too, so on such a
 * line it cannot say what the campaign's rule was left at. A login that changes the next-call
 * choice there says the rule again, which is harmless where it had not changed.
 *
 * The logins that turn either on go out before the unpause, so a rep coming back from a break is
 * never offered a caller who is not theirs on the way. Mid-call it changes that, which does nothing
 * to the call, and joins no line.
 */
function readyFor(
  me: AgentState,
  record: AgentStateRecord,
  mine: AgentLine[],
  missing: AgentLine[],
  next: boolean,
): Availability["ready"] {
  const joined = new Set(record.queues);
  const ownNow = new Set(record.ownCallsOnly ?? []);
  const nextNow = new Set(record.ownCallsNext ?? []);
  const theirs = new Set(mine.map((line) => line.name));
  const changes: AgentStep[] = [];
  for (const line of me.lines.filter((l) => joined.has(l.name))) {
    const nextChanges = next !== nextNow.has(line.name);
    const ownChanges = nextNow.has(line.name)
      ? nextChanges
      : theirs.has(line.name) !== ownNow.has(line.name);
    if (!nextChanges && !ownChanges) continue;
    changes.push({
      action: "login",
      queue: line.name,
      ...(ownChanges ? { ownCallsOnly: theirs.has(line.name) } : {}),
      ...(nextChanges ? { ownCallsNext: next } : {}),
    });
  }
  const keeps = (step: AgentStep) => step.ownCallsOnly === true || step.ownCallsNext === true;
  const paused = record.state === "paused";
  const engaged = ENGAGED.has(record.state);
  const join = (line: AgentLine): AgentStep => ({
    action: "login",
    queue: line.name,
    ...(theirs.has(line.name) ? { ownCallsOnly: true } : {}),
    ...(next ? { ownCallsNext: true } : {}),
  });
  const steps: AgentStep[] = [
    ...changes.filter(keeps),
    ...(paused ? [{ action: "unpause" as const, queue: null }] : []),
    ...changes.filter((step) => !keeps(step)),
    ...(engaged ? [] : missing.map(join)),
  ];
  if (changes.length > 0) {
    const everyCall = mine.length === 0 && nextNow.size > 0 && !next;
    return {
      steps,
      detail: paused
        ? "Back from your break"
        : everyCall
          ? "Take every call again"
          : "Take calls as your campaigns route them",
    };
  }
  const { detail } = readyWhileSignedIn(record.state, missing);
  if (paused || (missing.length > 0 && !engaged)) return { steps, detail };
  return { steps: [], detail };
}

/**
 * What the phone says about a call a caller made to a rep's own number (ADR-037): "Called your
 * number" on that rep's phone, "Called Maria Lopez's number" on anybody else's, whom the call
 * reached because Maria could not take it. Null for any other call. `nameOf` finds a colleague's
 * name by extension; one it cannot name is spelled as the extension.
 */
export function repNumberText(
  kind: string | null,
  meantFor: string | null | undefined,
  me: { user: string; domain: string } | null | undefined,
  nameOf: (extension: string) => string | null | undefined,
): string | null {
  if (kind !== "inbound" || !meantFor) return null;
  if (me && meantFor === `${me.user}@${me.domain}`) return "Called your number";
  const extension = meantFor.split("@")[0] ?? meantFor;
  return `Called ${nameOf(extension) ?? extension}'s number`;
}

/**
 * Ready for a rep signed in to a line already. It stays pressable while they are missing a line
 * they staff, as tap-ucaas keeps its Ready, and signs in to those lines and no others, one login
 * naming each: a login for a line they are in already is not a no-op, it ends their wrap-up there.
 * An unpause joins no line, so back from a break is an unpause and then the same logins.
 */
function readyWhileSignedIn(state: string, missing: AgentLine[]): Availability["ready"] {
  const logins = missing.map((line): AgentStep => ({ action: "login", queue: line.name }));
  const names = linesText(missing);
  if (state === "paused") {
    return {
      steps: [{ action: "unpause", queue: null }, ...logins],
      detail:
        missing.length > 0
          ? `Back from your break, and sign in to ${names}`
          : "Back from your break",
    };
  }
  if (missing.length === 0) return { steps: [], detail: "You are already taking calls" };
  if (ENGAGED.has(state)) {
    return { steps: [], detail: `Finish your call first, then sign in to ${names}` };
  }
  return { steps: logins, detail: `Sign in to ${names} too` };
}

/**
 * Which queries a hint on `GET /events` makes stale. The hint names a topic, not data
 * (`services/shared/state/live_topics.py`), so the console refetches whatever reads it. A topic
 * this list does not know — the platform shipped one ahead of the console — refreshes nothing.
 */
const HINTS: Record<string, readonly (readonly string[])[]> = {
  queue: [["acd"]],
  acd: [["acd"]],
  dialer: [["dialer"]],
  registration: [["endpoints"], ["phone", "directory"]],
  presence: [["phone", "directory"]],
  room: [["phone", "room"]],
};

export const HINTED_QUERIES: readonly (readonly string[])[] = [
  ...new Map(Object.values(HINTS).flat().map((key) => [key.join("/"), key])).values(),
];

export function queriesForHint(topic: string): readonly (readonly string[])[] {
  return HINTS[topic] ?? [];
}

/** The topic of one hint frame's payload, or null for a payload that is not one. */
export function hintTopic(data: string): string | null {
  try {
    const envelope: unknown = JSON.parse(data);
    if (envelope && typeof envelope === "object") {
      const topic = (envelope as { topic?: unknown }).topic;
      return typeof topic === "string" && topic ? topic : null;
    }
  } catch {
    /* not JSON: not a hint */
  }
  return null;
}

/** How long to wait before reopening a dropped hint stream: 1 s, doubling, at most 30 s. */
export function nextBackoff(previousMs: number | null): number {
  return previousMs === null ? 1_000 : Math.min(previousMs * 2, 30_000);
}

/**
 * When to refetch what a burst of hints made stale: once the burst has been quiet for `quietMs`
 * (tap-ucaas's queue monitor debounces its hints by the same 200 ms), and never later than
 * `maxWaitMs` after its first hint. The cap is for a busy floor, whose hints may never pause long
 * enough for a quiet gap — every refetch a hint started would otherwise be cancelled by the next
 * hint before it finished.
 */
export function nextFlushDelay(
  firstHintAt: number,
  now: number,
  quietMs = 200,
  maxWaitMs = 1_000,
): number {
  return Math.max(0, Math.min(quietMs, firstHintAt + maxWaitMs - now));
}

/** A call the ACD rang the rep for, as the event-tracker's record names it. */
export interface AcdCall {
  /** The phone's own call, or null when none of the phone's calls can be matched to it. */
  callId: string | null;
  /** The caller's channel: what a handoff and a disposition name. */
  callerChannelId: string;
  /** The caller's queue session: their stay in the line, a new one each time the ACD admits the
   *  call (a handoff to a closer is a second stay). */
  sessionId: string | null;
  state: "ringing" | "busy";
  /** The line the call came in on, when it is one the rep staffs. */
  line: AgentLine | null;
  queue: string | null;
  /** `inbound`, `outbound` (a lead the dialer reached) or `handoff`. */
  kind: string | null;
  handoffId: string | null;
  leadId: string | null;
  /** The caller's number as the ACD knows it. */
  callerNumber: string | null;
  /** The conference room the call lives in (ADR-029), once the rep has taken it. */
  room: string | null;
  /** The caller is on the hold a rep put them on in that room. */
  callerHeld: boolean;
  /** The rep whose own number the caller dialed, or whom a colleague named (ADR-037). */
  meantFor: string | null;
}

/**
 * The name of the line the ACD's call rings or is up on: the one line the record has in the call's
 * state.
 *
 * From the record's state in each line, not from its `queue`, which names the line of the last
 * change. On a dialer call the ACD puts the rep's other lines back to available straight after the
 * offer, so `queue` names one of those while the lead rings, and the phone would show that line's
 * campaign. The ACD offers one call at a time, so exactly one line is in the call's state, and
 * anything else names no line. A record written before per-line states existed has only `queue`.
 */
function callQueue(record: AgentStateRecord): string | null {
  const states = Object.entries(record.queueStates ?? {});
  if (states.length === 0) return record.queue ?? null;
  const onCall = states.filter(([, state]) => state === record.state);
  return onCall.length === 1 ? onCall[0]![0] : null;
}

/**
 * The call the ACD rang the rep for, and which of the phone's calls it is.
 *
 * The record names the rep's own leg (`channelId`), which is the call id the switch gives the
 * browser. Only an exact match associates a browser call with this assignment: switch events and
 * backend snapshots arrive independently, so even the phone's only call may be an unrelated call
 * arriving after the assigned leg ended. Leave it unmatched until matching information arrives.
 *
 * Only while the call rings or is up. The event-tracker clears a call's fields from the record the
 * moment it ends — which is when the disposition window opens — so the window reads its call from
 * the rep's sessions instead (`pendingDisposition`).
 */
export function acdCall(
  me: AgentState | null | undefined,
  calls: readonly { id: string }[],
): AcdCall | null {
  const record = me?.state;
  if (!me || !record?.callerChannelId) return null;
  if (record.state !== "ringing" && record.state !== "busy") return null;
  const matched = calls.find((c) => c.id === record.channelId);
  const queue = callQueue(record);
  return {
    callId: matched?.id ?? null,
    callerChannelId: record.callerChannelId,
    sessionId: record.sessionId ?? null,
    state: record.state as AcdCall["state"],
    line: me.lines.find((l) => l.name === queue) ?? null,
    queue,
    kind: record.kind ?? null,
    handoffId: record.handoffId ?? null,
    leadId: record.leadId ?? null,
    callerNumber: record.callerNumber ?? null,
    room: record.room ?? null,
    callerHeld: record.callerHeld === true,
    meantFor: record.meantFor ?? null,
  };
}

/**
 * Whether Hold on this phone call has to go through the ACD instead of the phone.
 *
 * A call the ACD connected lives in a conference room (ADR-029). The phone's own hold would hold
 * the rep's leg into that room and leave the caller listening to nobody: a room has no bridge
 * partner for the switch to play hold music to. So the ACD holds the caller in the room with their
 * line's music, and the rep's record says once it has (`callerHeld`).
 */
export function holdsInRoom(acd: AcdCall | null, callId: string): boolean {
  return acd !== null && acd.state === "busy" && acd.room !== null && acd.callId === callId;
}

/** The phone's calls that live in a room the ACD made, which a transfer that moves a bridge
 *  partner cannot take (`transferOptions`). */
export function callRoomIds(acd: AcdCall | null): string[] {
  return acd?.room && acd.callId ? [acd.callId] : [];
}

/** A consult handoff's private briefing, as this rep's own record tells it (ADR-027). */
export interface Briefing {
  handoffId: string;
  /** The other rep's extension. */
  withExtension: string | null;
  /** `setter` is briefing, `closer` is being briefed. */
  role: "setter" | "closer";
}

/**
 * The briefing this rep is in the middle of, or null.
 *
 * Neither side's state says so — the setter is `busy` on a call the ACD still counts as theirs, and
 * the closer is `busy` without the client — so it comes from the three consult fields the
 * event-tracker writes. Only while they are on a call: a record that kept the fields after one
 * ended would otherwise leave the buttons on screen.
 */
export function consultBriefing(me: AgentState | null | undefined): Briefing | null {
  const record = me?.state;
  if (!record?.consultHandoffId || record.state !== "busy") return null;
  return {
    handoffId: record.consultHandoffId,
    withExtension: record.consultWith ?? null,
    role: record.consultRole === "closer" ? "closer" : "setter",
  };
}

/** What a briefing takes out of the rep's hands on the call it is about, with the reason for
 *  each: `null` when this call is not the one being briefed. */
export interface BriefingLocks {
  /** Why Hold is not theirs to press while the briefing is on. */
  hold: string;
  /** Why End call is not theirs, or null when it is — the closer's, whose only way out it is. */
  endCall: string | null;
}

/**
 * The two controls a briefing takes over on the call it is about (ADR-027).
 *
 * Neither is blocked to be strict: both would do something other than what they say.
 *
 * * **Hold.** The ACD holds the client itself for the briefing, and turns a hold or an unhold
 *   down while one is on (`call_hold` in its matcher refuses a channel that is being briefed
 *   about). The tile would spin for its few seconds and nothing would happen.
 * * **End call**, for the SETTER. Hanging up mid-briefing ends nothing: the ACD hands the client
 *   to the closer who has just been briefed on them, which is what `Connect them & leave` says
 *   out loud. The closer keeps their End call — it is how they turn a briefing down, they have
 *   no buttons of their own, and the ACD gives the client back to the setter when they use it.
 */
export function briefingLocks(
  briefing: Briefing | null,
  acd: AcdCall | null,
  callId: string,
): BriefingLocks | null {
  if (briefing === null || acd === null || acd.callId !== callId) return null;
  return {
    hold: "The caller is on hold for the briefing",
    endCall:
      briefing.role === "setter" ? "Connect them, merge all, or take the caller back" : null,
  };
}

/**
 * Whether a line the rep is taking calls on answers its calls for them: its campaign asks for it
 * (`force_auto_answer` on `/acd/me`, which core-api works out from the lines they are signed in to),
 * and they are signed in. The phone shows it, and watches for a call it may have to answer. Which
 * calls it answers is `forcedAnswer`'s, by each call's own line.
 */
export function campaignAnswers(me: AgentState | null | undefined): boolean {
  return Boolean(me?.force_auto_answer) && availability(me!).signedIn;
}

/** The line the ACD's call rings on (`callQueue`), when it is one the rep staffs; null otherwise. */
export function ringingLine(me: AgentState | null | undefined): AgentLine | null {
  const record = me?.state;
  if (!me || record?.state !== "ringing") return null;
  const name = callQueue(record);
  return me.lines.find((line) => line.name === name) ?? null;
}

/**
 * The call the phone answers by itself because its campaign asks it to, or null.
 *
 * Only the ACD's call, as `acdCall` names it, only while it rings, and only when the line it rings on
 * belongs to a campaign that asks: a rep who staffs several campaigns answers the other campaigns'
 * calls themselves, and a colleague's call still waits for a click. Never over another call — the
 * rule this browser's own auto-answer keeps (`shouldAutoAnswer`), because Verto does not hold the
 * first call when a second is answered. And not while this browser's auto-answer is on, which
 * answers the call the moment it arrives: a second answer of the same call fails.
 */
export function forcedAnswer(
  me: AgentState | null | undefined,
  calls: readonly { id: string; direction: "inbound" | "outbound"; state: string }[],
  browserAnswers: boolean,
): string | null {
  const only = calls.length === 1 ? calls[0]! : null;
  if (browserAnswers || only === null) return null;
  const acd = acdCall(me, calls);
  if (acd?.state !== "ringing" || acd.callId !== only.id) return null;
  if (!ringingLine(me)?.force_auto_answer) return null;
  return only.direction === "inbound" && only.state === "ringing" ? only.id : null;
}

const CALL_KINDS: Record<string, string> = {
  inbound: "Inbound call",
  outbound: "A lead the dialer reached",
  handoff: "Handed off to you",
};

/** What kind of ACD call this is, in the rep's words. */
export function callKindText(kind: string | null): string {
  return (kind !== null && CALL_KINDS[kind]) || "A call from your line";
}

/**
 * The call a disposition window is for: the rep's newest queue session that reached them, while it
 * has no outcome. Sessions come newest first (`GET /acd/sessions`). Only the newest connected one
 * counts — an older call without an outcome is not what this window is for — and only the rep's
 * own, matched on the realm as well as the extension, because a supervisor's sessions are the whole
 * tenant's.
 */
export function pendingDisposition(
  sessions: readonly QueueSession[],
  agent: { user: string; domain: string } | null,
): QueueSession | null {
  if (!agent) return null;
  const newest = sessions.find(
    (s) =>
      s.agent_user === agent.user && s.agent_domain === agent.domain && s.bridge_started_at !== null,
  );
  return newest && newest.disposition === null ? newest : null;
}
